import React from "react";
import flagMap from "../../../assets/utils/flagMap";
import regionMap from "../../../assets/utils/regionMap";
import crossIcon from "../../../assets/cross-small.png";
import trashUndoIcon from "../../../assets/trash-undo.png";
import "./EsimCard.css";

const EsimCard = ({ esim, selected = false, onToggle, isDeleted = false, onDeletePress, onRestorePress }) => {
  const isUnlimited = esim.data_left < 0 || esim.data_limit < 0;

  const formatDataSize = (mbValue) => {
    if (!mbValue) return "0 MB";
    if (mbValue >= 1024) {
      return `${(mbValue / 1024).toFixed(1)} GB`;
    }
    return `${mbValue.toFixed(0)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getFlagSrc = () => {
    const countryCode = esim.country_id?.toLowerCase();
    const regionCode = esim.region_id?.toLowerCase();

    if (countryCode && flagMap[countryCode]) {
      return flagMap[countryCode];
    }

    if (regionCode && regionMap[regionCode]) {
      return regionMap[regionCode];
    }

    console.warn(`No flag found for country: ${esim.country_id}, region: ${esim.region_id}`); // debug
    return "/icons/flags/default.png";
  };

  const getStatusConfig = () => {
    const status = esim.status?.toLowerCase() || "unknown";

    // Derived "Ready" state: Inactive but already installed
    if (status === "inactive" && esim.installed) {
      return {
        label: "Ready",
        bgColor: "#f1ebff",
        textColor: "#6e3fff",
      };
    }

    switch (status) {
      case "active":
        return {
          label: "Active",
          bgColor: "rgba(34, 197, 94, 0.15)",
          textColor: "#22c55e",
        };
      case "inactive":
        return {
          label: "Inactive",
          bgColor: "rgba(61, 128, 255, 0.15)",
          textColor: "#3d80ff",
        };
      case "archived":
        return {
          label: "Archived",
          bgColor: "rgba(107, 114, 128, 0.15)",
          textColor: "#6b7280",
        };
      case "terminated":
        return {
          label: "Terminated",
          bgColor: "rgba(255, 59, 92, 0.15)",
          textColor: "#ff3b5c",
        };
      case "retired":
        return {
          label: "Retired",
          bgColor: "rgba(123, 129, 144, 0.15)",
          textColor: "#7b8190",
        };
      case "expired":
        return {
          label: "Expired",
          bgColor: "rgba(255, 159, 67, 0.15)",
          textColor: "#ff9f43",
        };
      default:
        return {
          label: status.charAt(0).toUpperCase() + status.slice(1),
          bgColor: "rgba(59, 130, 246, 0.15)",
          textColor: "#3b82f6",
        };
    }
  };

  const isRegion = !!esim.region_id && !esim.country_id;
  const { label: statusLabel, bgColor: statusBgColor, textColor: statusTextColor } = getStatusConfig();

  const statusStr = esim.status?.toLowerCase() || "unknown";
  const isProgressBarGray = ["retired", "expired", "terminated"].includes(statusStr);

  return (
    <div className={`esim-card ${selected ? "esim-card--selected" : ""}`} onClick={() => onToggle?.(esim)}>
      <div className="esim-card-header">
        <button
          type="button"
          className={`esim-radio ${selected ? "esim-radio--checked" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.(esim);
          }}
          aria-pressed={selected}
        >
          <span className="esim-radio-circle" />
        </button>

        <div className="esim-card-header-right">
          <span className="status-badge" style={{ backgroundColor: statusBgColor, color: statusTextColor }}>
            {statusLabel}
          </span>

          {isDeleted ? (
            <button
              type="button"
              className="esim-restore-btn"
              onClick={(e) => {
                e.stopPropagation();
                onRestorePress?.(esim);
              }}
              aria-label="Restore eSIM"
            >
              <img src={trashUndoIcon} alt="restore" className="esim-action-icon" />
            </button>
          ) : (
            <button
              type="button"
              className="esim-delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                onDeletePress?.(esim);
              }}
              aria-label="Delete eSIM"
            >
              <img src={crossIcon} alt="delete" className="esim-action-icon" />
            </button>
          )}
        </div>
      </div>

      <div className="esim-label-row">
        <span className="esim-for">eSIM for</span>
      </div>

      <div className="esim-location">
        <img
          src={getFlagSrc()}
          alt={`${esim.country_name || esim.region_name || "eSIM"}`}
          className={`flag-icon ${isRegion ? "region-icon" : ""}`}
        />
        <span className="location-name">{esim.country_name || esim.region_name || "Global"}</span>
      </div>

      <div className="progress-section">
        <div className="progress-bar">
          <div
            className={`progress-fill ${isProgressBarGray ? "progress-fill--gray" : ""}`}
            style={{
              width: isUnlimited
                ? "100%"
                : esim.data_left && esim.data_limit
                  ? `${Math.max((esim.data_left / esim.data_limit) * 100, 5)}%`
                  : "100%",
            }}
          />
        </div>
        <span className="data-usage">
          {isUnlimited ? "Unlimited" : `${formatDataSize(esim.data_left)} / ${formatDataSize(esim.data_limit)}`}
        </span>
      </div>

      <div className="valid-date">Activate before {formatDate(esim.activate_before)}</div>

      {/* Installed / Not Installed Banner */}
      <div className={`esim-installed-banner ${esim.installed && !isProgressBarGray ? "installed" : "not-installed"}`}>
        {esim.installed ? "Installed" : "Not Installed"}
      </div>
    </div>
  );
};

export default EsimCard;
